import { useContext, useEffect, useState } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { SiteContext } from '../../../context/SiteContext';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import {
  populateSelectOptions,
  setCountryCode
} from '../../common/HelperMethods';
import {
  InsuranceVehicleVendor,
  InsuranceVehicleNewUsed,
  PageAbsolutePath,
  InsuranceLoadingMessage
} from '../../../consts/Common';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState,
  KeyValuePair,
  QuoteStatus,
  SelectOpt,
  PaymentMethod
} from '../../common/CommonTypes';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import TextInput from '../../common/TextInput/TextInput';
import List from '../../common/Widgets/List';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MileageRewardProduct from './MileageRewardProduct';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { PagesWithDropdown, Source } from '../../common/Constants';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import PaymentSummary from './PaymentSummary';
import ViewDocument from '../../common/ViewDocument/ViewDocument';
import InsuranceDocumentLink from './InsuranceDocumentLink';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import BusinessTermsAncillaryPage from './BusinessTermsAncillaryPage';
import PopupComponent from '../../common/PopupComponent';
import { useErrorBoundary } from 'react-error-boundary';

const YourQuotePage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const token = site.user.token;
  const { brandDisplayName, brandName } = site.brand;
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { quotesAndRenewalsNumber } = site.brand.brandPhoneNumbers;
  const [loading, setLoading] = useState(false);
  const [showPopupComponent, setShowPopupComponent] = useState(false);

  const {
    personalDetails,
    vehicleInfo,
    insuranceProducts,
    coverDetails,
    paymentDetails,
    staticData,
    insuranceNavigationStatus,
    savingQuote,
    source,
    dealerHomeOptions
  } = journey;
  const { firstName } = personalDetails;
  const { coverStartDate } = coverDetails;
  const { commonEvents } = pageEvents;
  const { setSavingQuote, onPageChange, setQuote, setStaticData } =
    commonEvents;
  const { saveQuote } = useCarInsuranceController();

  const { estimatedMileage } = coverDetails;

  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const [showModalPopup, setShowModalPopup] = useState(false);
  const [voluntaryExcessOptions, setVoluntaryExcessOptions] = useState<
    SelectOpt[]
  >([]);
  const [showPayByPhonePopup, setShowPayByPhonePopup] = useState(false);

  const {
    voluntaryExcess,
    policyDocumentId,
    ipidStatementDocumentId,
    showNCDDocument
  } = insuranceProducts;

  const { getYourQuotePageDropdownValues } = useCarInsuranceController();

  const isQualifiedForMileageReward = () => {
    const mileage = Number(estimatedMileage.replace(',', ''));

    return (
      vehicleInfo.manufacturer === 'Renault' &&
      vehicleInfo.model === 'Austral' &&
      mileage < 10000
    );
  };

  const productBenefitsFirstType: string[] = [
    `${brandDisplayName} Authorised Bodyshop repairs using genuine ${brandDisplayName} parts`,
    'Comprehensive UK & EU cover',
    'Personal accident cover',
    'Medical cover',
    'Personal possessions cover',
    'Windscreen damage cover with no loss of your no claims discount',
    '24-hour accident recovery',
    `A brand new ${brandDisplayName} if yours is written off or stolen within 12 months of its new registration date`
  ];

  const productBenefitsSecondType: string[] = [
    'Comprehensive cover',
    `${brandDisplayName} Approved Repairers`,
    'Comprehensive EU cover',
    'Personal Accident cover',
    'Medical cover',
    'Personal possession cover',
    'Windscreen damage cover'
  ];

  const productBenefitsOriginalType: string[] = [
    'Comprehensive cover',
    'Preserved Warranty',
    'Comprehensive EU cover',
    'Personal Accident cover',
    'Medical cover',
    'Personal possession cover',
    'Windscreen damage cover'
  ];

  const getQuoteListItems = () => {
    switch (brandName) {
      case 'original':
        return productBenefitsOriginalType;
      case 'kia':
      case 'dacia':
      case 'renault':
      case 'nissan':
      case 'lotus':
        return productBenefitsFirstType;
      default:
        return productBenefitsSecondType;
    }
  };

  //Moving this into the front-end for now, will probably be set in kentico when that's wired up
  const quoteListItems1 = getQuoteListItems().slice(0, 4);
  const quoteListItems2 = getQuoteListItems().slice(4);

  const getYourQuotePageDropdownValuesSuccess = (
    dropdownValues: KeyValuePair[]
  ) => {
    const newValues = [
      {
        page: PagesWithDropdown.YourQuotePage,
        controlName: 'voluntary-excess',
        values: populateSelectOptions(dropdownValues)
      }
    ];

    setStaticData(staticData ? staticData.concat(...newValues) : newValues);
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (insuranceProducts.annualPayment === 0) {
      //no product found so take to referral page
      isDealerLoggedIn
        ? navigate(PageAbsolutePath.Insurance.DealerReferral)
        : navigate(PageAbsolutePath.Insurance.Referral);
    } else {
      const dropdownValues = staticData?.find(
        (d) => d.page === PagesWithDropdown.YourQuotePage
      )?.values;

      !dropdownValues &&
        getYourQuotePageDropdownValues(
          token,
          getYourQuotePageDropdownValuesSuccess,
          onBackendFailure
        );
    }
  }, []);

  useEffect(() => {
    const options = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.YourQuotePage &&
        d.controlName === 'voluntary-excess'
    )?.values;

    options
      ? setVoluntaryExcessOptions(options)
      : setVoluntaryExcessOptions([]);
  }, [staticData]);

  const saveFullQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      staticData: [...staticData],
      insuranceNavigationStatus: { ...insuranceNavigationStatus }
    });
    setLoading(false);
  };

  const saveFullQuoteFailure = (error: string) => {
    console.log(`Get cover details error: ${error}`);
    //failed to save quote so take to referral page
    isDealerLoggedIn
      ? navigate(PageAbsolutePath.Insurance.DealerReferral)
      : navigate(PageAbsolutePath.Insurance.Referral);
  };

  const onChangeVoluntryExcessValue = (value: string) => {
    if (
      value &&
      voluntaryExcess !== value &&
      value != SelectInputHelper.NullValue()
    ) {
      setLoading(true);
      saveQuote(
        {
          ...journey,
          quoteStatus: QuoteStatus.Full,
          insuranceProducts: { ...insuranceProducts, voluntaryExcess: value }
        },
        site.user.token,
        saveFullQuoteSuccess,
        saveFullQuoteFailure
      );
    }
  };

  const hasMileageRewardProduct = isQualifiedForMileageReward();

  const [includeMileageRewardProduct, setIncludeMileageRewardProduct] =
    useState(hasMileageRewardProduct);

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.ConfirmationPage,
      site.countryCode
    );
    navigate(path);
  };

  const onContinueClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.VehicleDetails,
      site.countryCode
    );
    navigate(path);
  };

  const onBuyClick = () => {
    if (vehicleInfo.vehicle?.vrn) {
      if (
        vehicleInfo.vendor !== InsuranceVehicleVendor.FranchiseDealer &&
        vehicleInfo.isItNewOrUsed !== InsuranceVehicleNewUsed.New
      ) {
        setShowPayByPhonePopup(true);
      } else {
        const path: string = setCountryCode(
          PageAbsolutePath.Insurance.OptionalCover,
          site.countryCode
        );
        navigate(path);
        onPageChange(PageAbsolutePath.Insurance.YourQuote, true);
      }
    } else {
      setShowModalPopup(true);
    }
  };
  let policyStartDate: string | undefined = undefined;
  if (coverStartDate && coverStartDate !== undefined) {
    policyStartDate = new Date(coverStartDate).toLocaleDateString('en-GB');
  }

  const handleOnClickDealerAdmin = (): void => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.DealersAdminPage,
      site.countryCode
    );
    navigate(path);
  };

  const getVoluntaryExcess = (str: string): number => {
    const voluntaryExcessAmount = parseInt(
      voluntaryExcessOptions.find((x) => x.value === str)?.value ?? '0'
    );
    return voluntaryExcessAmount;
  };

  const voluntaryExcessValue = getVoluntaryExcess(voluntaryExcess);

  const annualCompulsoryPayment = `£${insuranceProducts.excessCompulsoryPayment}`;
  const annualTotal = `£${insuranceProducts.excessCompulsoryPayment + voluntaryExcessValue}`;
  const listIcon = <FontAwesomeIcon icon={faCheck} listItem />;

  const addRemoveProduct = () => {
    console.log(
      includeMileageRewardProduct
        ? 'Mileage reward product removed'
        : 'Mileage reward product added'
    );
    setIncludeMileageRewardProduct(!includeMileageRewardProduct);
  };

  const isDealerLoggedIn =
    site.user.isDealer &&
    CommonValidation.isNotEmpty(site.user.insuranceDealerLoginUserName);

  const renderBusinessTermsAncillaryPage = () => {
    return <BusinessTermsAncillaryPage isShowingInPopup />;
  };

  const onClosePopup = () => {
    setShowPopupComponent(false);
  };

  const getMessage = () => {
    if (source === Source.CustomerSelfRegistration) {
      if (
        dealerHomeOptions?.includeExcessReturn === true &&
        dealerHomeOptions?.includeDriveaway === true
      ) {
        return 'Thank you for registering. Your Dealer will activate your Excess Return voucher and Driveaway Insurance ready for vehicle collection';
      }

      if (dealerHomeOptions?.includeExcessReturn === true) {
        return 'Thank you for registering. Your Dealer will activate your Excess Return voucher ready for vehicle collection';
      }

      if (dealerHomeOptions?.includeDriveaway === true) {
        return 'Thank you for registering. Your Dealer will activate your Driveaway Insurance ready for vehicle collection';
      }
    }

    if (source === Source.Mobilise) {
      if (vehicleInfo.vehicle?.vrn) {
        return `${firstName} we have emailed your voucher to you, so please check that everything is correct`;
      }

      if (!vehicleInfo.vehicle?.vrn) {
        return `${firstName} You are not covered yet. We'll email your Excess Return Voucher once your new vehicle's registration number is confirmed`;
      }
    }

    return '';
  };

  const getMessageTitle = () => {
    if (source === Source.Mobilise) {
      if (vehicleInfo.vehicle?.vrn) {
        return `${firstName} Your Excess return Voucher is ready`;
      }

      if (!vehicleInfo.vehicle?.vrn) {
        return `${firstName} Thank you for registering for Excess Return`;
      }
    }

    return '';
  };

  return (
    <div>
      <LoadingContainer loading={loading} message={InsuranceLoadingMessage}>
        <div>
          {isDealerLoggedIn && (
            <ButtonContinue
              name='btn-dealer admin'
              text='Dealer Admin'
              onClick={handleOnClickDealerAdmin}
              additionalClassNames='btn-save m1-bottom'
            />
          )}
          <h4 className='section-header m1-top'>
            Exclusive annual insurance cover for your{' '}
            {vehicleInfo.vehicle?.modelType !== undefined &&
            vehicleInfo.vehicle?.modelType !== ''
              ? vehicleInfo.vehicle?.modelType
              : vehicleInfo.manufacturer + ' ' + vehicleInfo.model}
          </h4>
          <div className='m2-top'>
            <PaymentSummary
              headerText={`${firstName}, your quote is`}
              policyStartDate={policyStartDate}
              disableTabs={false}
              salesTelephoneNumber={
                site.brand.brandPhoneNumbers.quotesAndRenewalsNumber
              }
              message={getMessage()}
              messageTitle={getMessageTitle()}
              hideMonthlyIfZero={false}
            />
          </div>
          {(paymentDetails.selectedPaymentMethod === PaymentMethod.annually ||
            insuranceProducts.monthlyInstallment > 0) && (
            <>
              {hasMileageRewardProduct && (
                <MileageRewardProduct addRemoveProduct={addRemoveProduct} />
              )}
              <h4 className='section-header m2-top'>Manage Your Excesses</h4>
              <div className='form-container'>
                <div className='one-col'>
                  <div>
                    <div className='control-label'>Compulsory</div>
                    <div className='control'>
                      <TextInput
                        placeholder=''
                        name='compulsory'
                        className='input-text w250'
                        value={annualCompulsoryPayment}
                        onChange={() => {}}
                        error=''
                        readOnly
                      />
                    </div>
                  </div>
                </div>
                <div className='one-col'>
                  <div>
                    <div className='control-label'>Voluntary</div>
                    <div className='control'>
                      <SelectInput
                        name='voluntaryExcess'
                        className='input-text w250'
                        value={voluntaryExcess}
                        onChange={(value: string) => {
                          onChangeVoluntryExcessValue(value);
                        }}
                        options={voluntaryExcessOptions}
                        forceDefault
                        addDefaultOption
                      />
                    </div>
                  </div>
                </div>
                <div className='one-col'>
                  <div>
                    <div className='control-label'>Total</div>
                    <div className='control'>
                      <TextInput
                        placeholder=''
                        name='total'
                        className='input-text w250'
                        value={annualTotal}
                        onChange={() => {}}
                        error=''
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
              <h4 className='section-header m2-top'>Included in your quote</h4>
              <p className='m1-top'>
                We know your {site.brand.brandDisplayName} inside and out, which
                is why we guarantee the repair benefits that most insurers
                don't.
              </p>
              <div className='flex-row'>
                <div className='m2-right'>
                  <List
                    id='quote-list-table1'
                    listItems={quoteListItems1}
                    icon={listIcon}
                  />
                </div>
                <List
                  id='quote-list-table2'
                  listItems={quoteListItems2}
                  icon={listIcon}
                />
              </div>
              <h4 className='section-header m1-top m1-bottom'>
                Your Documents
              </h4>
              <p>Your Annual insurance will be underwritten by ABC Insurance</p>
              <div className='even-space-out flex-row form-nav'>
                <InsuranceDocumentLink
                  documentId={policyDocumentId}
                  displayText='Policy document'
                  additionalClasses='inherited'
                />
                <InsuranceDocumentLink
                  documentId={ipidStatementDocumentId}
                  displayText='IPID statement'
                  additionalClasses='inherited'
                />
                {showNCDDocument && (
                  <ViewDocument
                    path={`docs/Guide to NCD.pdf`}
                    documentName='Important NCD Information'
                    additionalClasses='auto-width inherited'
                  />
                )}
                <Link
                  id='lnkTermsOfBusinessInText'
                  className='in-text inherited'
                  onClick={() => setShowPopupComponent(true)}
                  to=''>
                  Terms of Business
                </Link>
              </div>
            </>
          )}
          <div className='even-space-out flex-row form-nav'>
            <ButtonBack
              name='btn-back'
              text='Back'
              onClick={onBackClick}
              additionalClassNames='btn-back'
            />
            <ButtonContinue
              name='btn-save-later'
              text='Save For Later'
              onClick={() => setSavingQuote(true)}
              additionalClassNames='btn-save'
              disabled={savingQuote}
            />
            <ButtonContinue
              name='btn-buy'
              text='Buy'
              onClick={onBuyClick}
              additionalClassNames='btn-continue'
              disabled={
                paymentDetails.selectedPaymentMethod ===
                  PaymentMethod.monthly &&
                insuranceProducts.monthlyInstallment === 0
              }
            />
          </div>
        </div>
      </LoadingContainer>
      <Modal
        role='modal-popup'
        isOpen={showModalPopup}
        className={`insurance-${site.brand.brandName}`}
        toggle={() => {
          setShowModalPopup(false);
        }}>
        <ModalHeader
          toggle={() => {
            setShowModalPopup(false);
          }}>
          You missed something
        </ModalHeader>
        <ModalBody>Please enter the vehicle registration</ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='btn-continue'
            text='Continue'
            additionalClassNames='btn-ok'
            onClick={onContinueClick}
          />
          <ButtonContinue
            name='btn-cancel'
            text='Cancel'
            additionalClassNames='btn-cancel'
            onClick={() => {
              setShowModalPopup(false);
            }}
          />
        </ModalFooter>
      </Modal>
      <Modal
        role='pay-by-phone-modal-popup'
        className={`insurance-${site.brand.brandName}`}
        isOpen={showPayByPhonePopup}
        toggle={() => setShowPayByPhonePopup(false)}>
        <ModalHeader toggle={() => setShowPayByPhonePopup(false)}>
          We're Sorry
        </ModalHeader>
        <ModalBody>
          {`Online payment is not available, please call: ${quotesAndRenewalsNumber}`}
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='modal-btn-continue'
            text='Close'
            onClick={() => setShowPayByPhonePopup(false)}
            additionalClassNames='btn-ok'
          />
        </ModalFooter>
      </Modal>
      <PopupComponent
        showPopupComponent={showPopupComponent}
        renderComponent={renderBusinessTermsAncillaryPage}
        onClosePopup={onClosePopup}
        popupTitle='Terms of Business'
      />
    </div>
  );
};

export default YourQuotePage;
